import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { MoreVertical } from "lucide-react";

export interface OverflowMenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  tone?: "default" | "danger";
  onSelect: () => void;
}

/** Menu "mais acoes" atras de um botao MoreVertical — usado para acoes
 * destrutivas/pouco frequentes (ex.: excluir dados do titular) que nao devem
 * ficar como botao solto no meio da tela. Acessivel: Escape fecha e devolve
 * foco ao botao, clique fora fecha, foco vai para o primeiro item ao abrir,
 * cada item e um <button role="menuitem"> real. */
export function OverflowMenu({ items, label = "Mais ações" }: { items: OverflowMenuItem[]; label?: string }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!aberto) return;

    firstItemRef.current?.focus();

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setAberto(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [aberto]);

  return (
    <div className="overflow-menu" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--ghost btn--icon"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={menuId}
        aria-label={label}
        onClick={() => setAberto((v) => !v)}
      >
        <MoreVertical className="icon" size={20} aria-hidden="true" />
      </button>
      {aberto && (
        <ul className="overflow-menu__list" role="menu" id={menuId} aria-label={label}>
          {items.map((item, index) => (
            <li key={item.key} role="none">
              <button
                ref={index === 0 ? firstItemRef : undefined}
                type="button"
                role="menuitem"
                className={`overflow-menu__item${item.tone === "danger" ? " overflow-menu__item--danger" : ""}`}
                onClick={() => {
                  setAberto(false);
                  triggerRef.current?.focus();
                  item.onSelect();
                }}
              >
                {item.icon}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
